import { useState } from 'react';
import { ImageWithSkeleton } from '@/components/ImageWithSkeleton';
import { IPortfolioImagesItem } from '@/types/api/companies';
import { PushPinOutlined } from '@mui/icons-material';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Swiper, { Navigation, Pagination, Thumbs } from 'swiper';
import { Swiper as MySwiper, SwiperSlide } from 'swiper/react';

interface ICarouselPortfolioProps {
  imagesList: Array<IPortfolioImagesItem>;
  isPinned: boolean;
  activeThumbs?: boolean;
  altTitle: string;
}

export const CarouselPortfolio = ({
  imagesList,
  isPinned,
  activeThumbs,
  altTitle,
}: ICarouselPortfolioProps) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<Swiper | null>(null);
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      sx={{
        display: mdUp && activeThumbs ? 'flex' : 'block',
        justifyContent: mdUp && activeThumbs ? 'space-between' : '',
        maxWidth: mdUp && activeThumbs ? '100%' : '320px',
        margin: mdUp && activeThumbs ? '' : '0 auto',
      }}
    >
      <Box
        sx={{
          '& .swiper-wrapper': {
            display: 'flex',
            alignItems: 'center',
            height: '320px',
          },
          '& .swiper-button-prev, & .swiper-button-next': {
            color: 'primary.main',
          },
          '& .swiper-pagination-bullet-active': {
            backgroundColor: 'primary.main',
          },
        }}
      >
        <MySwiper
          tag="section"
          spaceBetween={30}
          centeredSlides
          loop={true}
          pagination={{
            dynamicBullets: true,
          }}
          thumbs={{ swiper: thumbsSwiper }}
          navigation={true}
          modules={[Pagination, Navigation, Thumbs]}
          style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: '12px',
            backgroundColor: '#e9e9e9',
            height: '320px',
            width: '320px',
          }}
        >
          {imagesList.map(image => {
            return (
              <SwiperSlide
                key={image.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isPinned && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px',
                      backgroundColor: 'background.paper',
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      borderRadius: '50%',
                      border: '0.5px solid #A4A4A4',
                      zIndex: 1,
                    }}
                  >
                    <PushPinOutlined
                      fontSize="small"
                      sx={{ transform: 'rotate(45deg)', color: '#A4A4A4' }}
                    />
                  </Box>
                )}
                <ImageWithSkeleton
                  src={image.photo}
                  alt={`Item ${altTitle}`}
                  sx={{
                    objectFit: 'scale-down',
                  }}
                />
              </SwiperSlide>
            );
          })}
        </MySwiper>
      </Box>
      {mdUp && activeThumbs && (
        <Box
          sx={{
            '& .swiper-wrapper': {
              display: mdUp ? 'grid' : 'flex',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '40px',
            },
            width: '450px',
          }}
        >
          <MySwiper
            tag="article"
            onSwiper={swiper => setThumbsSwiper(swiper)}
            spaceBetween={10}
            slidesPerView={4}
            loop
            allowTouchMove={false}
          >
            {imagesList.map(image => {
              return (
                <SwiperSlide
                  key={image.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '100px',
                    maxHeight: '100px',
                    overflow: 'hidden',
                    backgroundColor: '#e9e9e9',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  <ImageWithSkeleton
                    src={image.photo}
                    alt={`Item ${altTitle}`}
                    object-fit="contain"
                    width="100%"
                  />
                </SwiperSlide>
              );
            })}
          </MySwiper>
        </Box>
      )}
    </Box>
  );
};
